/**
 * BatchInclusionToggle — the two batch-injection opt-outs.
 *
 * BLU-777 / closes BLU-632: this used to be ONE checkbox over one backend flag
 * (`includePriorityAndRedos`) gating BOTH injections. Luisa, on Slack:
 * "re-dos and requeues are not [separate]" — she was describing exactly that
 * boolean. It is now two, each with its own live count:
 *
 *   Redos      → pending redo garments queued at this location
 *                (createBatch Step 3c, `getPendingRedos`)
 *   Re-queued  → priority-queued / re-routed ORDERS at this location
 *                (createBatch Step 3d, `getPriorityQueuedOrders`)
 *
 * ⚠ COPY IS DELIBERATELY "Re-queued orders", NOT "Priority orders". There is a
 * batch SCOPE called Priority in the same UI, and the two see different sets: the
 * scope reads every accessible org at a shared 3PL location, this injection reads
 * the caller's org only (`getPriorityQueuedOrders` is single-org). Naming them
 * the same word would promise they agree. Brett's call, 2026-08-12: keep the gap,
 * name the two things distinctly. Widening the injection is a separate ticket.
 *
 * Both default ON (legacy behaviour). Each subline is state-conditional so it
 * always describes the CURRENT effect, and is wired via aria-describedby.
 *
 * Inventory-app design language (apps/frontend/DESIGN.md): single brand-purple
 * accent (the checked fill), §5 motion timings.
 */

import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface BatchInclusionToggleProps {
  /** Pull pending REDO garments into this batch (default legacy = true). */
  includeRedos: boolean;
  onIncludeRedosChange: (checked: boolean) => void;
  /** Pull priority-queued / re-routed ORDERS into this batch (default legacy = true). */
  includeRequeued: boolean;
  onIncludeRequeuedChange: (checked: boolean) => void;
  /**
   * Pending redo garments queued at this location. Drives the redo subline count.
   * Preview mode passes `summary.totalRedoItems`; browse mode has no preview
   * loaded, so it passes the count from the scope resolver's own read.
   */
  redoCount: number;
  /**
   * BLU-787: the PRE-cap count of pending redo garments (`pendingRedoTotal`),
   * so the redo row can say when the cap left some behind.
   *
   * ⚠ Only browse mode has this figure — it comes from `resolveBatchScopes`,
   * which the scoped-preview screen never calls (it derives `redoCount` from
   * `summary.totalRedoItems` instead). Leave undefined there; no trim line is
   * better than fabricating one from a number the preview doesn't have.
   *
   * `redoCount` itself stays the post-cap figure the existing "Adds N pending
   * redo garments" copy already promises — this is an ADDITIONAL line, never
   * a redefinition of `redoCount`.
   */
  redoTotalPending?: number;
  /**
   * Re-queued ORDERS waiting at this location, when known. `undefined` renders
   * the countless copy rather than a misleading "0".
   */
  requeuedCount?: number;
  /** Disable interaction (e.g. while the batch is being created). */
  disabled?: boolean;
  /** Unique id prefix so labels/checkboxes/sublines associate across mount points. */
  id?: string;
  className?: string;
  /**
   * 'default' (list-style, left-aligned) — inside a wider card with other
   * left-aligned rows.
   * 'floating' — the standalone floating pill mount (OrdersPage sticky Create
   * Batch bar) has nothing to align against, so it centers and wraps narrower.
   */
  variant?: 'default' | 'floating';
}

export function BatchInclusionToggle({
  includeRedos,
  onIncludeRedosChange,
  includeRequeued,
  onIncludeRequeuedChange,
  redoCount,
  redoTotalPending,
  requeuedCount,
  disabled = false,
  id = 'batch-include-extras',
  className,
  variant = 'default',
}: BatchInclusionToggleProps) {
  const { t } = useTranslation('production');
  const isFloating = variant === 'floating';

  const redoId = `${id}-redos`;
  const requeuedId = `${id}-requeued`;
  const redoDescId = `${redoId}-desc`;
  const requeuedDescId = `${requeuedId}-desc`;

  const redoSubline = !includeRedos
    ? t('batch.includeExtras.redosOff')
    : redoCount > 0
      ? t('batch.includeExtras.redosOnWithCount', { count: redoCount })
      : t('batch.includeExtras.redosOnNone');

  // BLU-787: `pendingRedoCount` above is already the post-cap figure the
  // subline describes. This is an ADDITIONAL note, not a redefinition — see
  // the `redoTotalPending` docblock.
  const redoTrimmedCount =
    includeRedos && redoTotalPending !== undefined
      ? Math.max(0, redoTotalPending - redoCount)
      : 0;

  const requeuedSubline = !includeRequeued
    ? t('batch.includeExtras.requeuedOff')
    : requeuedCount === undefined
      ? t('batch.includeExtras.requeuedOn')
      : requeuedCount > 0
        ? t('batch.includeExtras.requeuedOnWithCount', { count: requeuedCount })
        : t('batch.includeExtras.requeuedOnNone');

  return (
    <div className={cn('space-y-3', isFloating && 'text-center', className)}>
      <Row
        id={redoId}
        descId={redoDescId}
        checked={includeRedos}
        onCheckedChange={onIncludeRedosChange}
        disabled={disabled}
        label={t('batch.includeExtras.redosLabel')}
        subline={redoSubline}
        trimNote={
          redoTrimmedCount > 0
            ? t('batch.includeExtras.redosTrimmedNote', { count: redoTrimmedCount })
            : undefined
        }
        isFloating={isFloating}
      />
      <Row
        id={requeuedId}
        descId={requeuedDescId}
        checked={includeRequeued}
        onCheckedChange={onIncludeRequeuedChange}
        disabled={disabled}
        label={t('batch.includeExtras.requeuedLabel')}
        subline={requeuedSubline}
        isFloating={isFloating}
      />
    </div>
  );
}

function Row({
  id,
  descId,
  checked,
  onCheckedChange,
  disabled,
  label,
  subline,
  trimNote,
  isFloating,
}: {
  id: string;
  descId: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
  label: string;
  subline: string;
  /** BLU-787: cap-trim note, e.g. redos left behind by MAX_REDOS_PER_BATCH. */
  trimNote?: string;
  isFloating: boolean;
}) {
  return (
    <div className={cn('flex items-start gap-2.5', isFloating && 'flex-col items-center gap-1.5')}>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={disabled}
        aria-describedby={descId}
        className={cn(!isFloating && 'mt-0.5')}
      />
      <div className={cn('space-y-0.5', isFloating && 'space-y-1')}>
        <Label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-sm font-medium leading-snug',
            isFloating && 'block text-pretty'
          )}
        >
          {label}
        </Label>
        <p
          id={descId}
          aria-live="polite"
          className={cn(
            'text-xs text-muted-foreground transition-colors duration-200 ease-out motion-reduce:transition-none',
            isFloating && 'text-pretty max-w-[26ch] mx-auto'
          )}
        >
          {subline}
        </p>
        {trimNote && (
          <p
            className={cn(
              'text-xs text-amber-700 dark:text-amber-400 text-pretty',
              isFloating && 'max-w-[26ch] mx-auto'
            )}
          >
            {trimNote}
          </p>
        )}
      </div>
    </div>
  );
}
